import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { Play, Square, Clock, Car, User, Wrench, AlertCircle } from 'lucide-react';

export default function WoClockPage() {
  const [searchParams] = useSearchParams();
  const woId = searchParams.get('id') || '';

  const [woInfo, setWoInfo] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [pin, setPin] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [empData, setEmpData] = useState(null);
  const [storedPin, setStoredPin] = useState('');
  const [clockStatus, setClockStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);

  // Load work order info + employees
  useEffect(() => {
    if (!woId) { setError('ID de fiche manquant'); return; }
    Promise.all([
      axios.get(`${API}/public/wo-clock/info/${woId}`),
      axios.get(`${API}/public/wo-clock/employees`),
    ]).then(([woRes, empRes]) => {
      setWoInfo(woRes.data);
      setEmployees(empRes.data);
    }).catch(() => setError('Fiche de travail introuvable'));
  }, [woId]);

  // Auto-submit PIN when 4 digits
  useEffect(() => {
    if (pin.length === 4 && selectedEmployee && !loading && !authenticated) {
      verifyPin();
    }
  }, [pin]);

  const verifyPin = async () => {
    if (!selectedEmployee || pin.length !== 4) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/wo-clock/verify-pin`, {
        employee_id: selectedEmployee, pin_code: pin
      });
      setEmpData(res.data);
      setStoredPin(pin);
      setAuthenticated(true);
      loadStatus(selectedEmployee);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code PIN incorrect');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = useCallback(async (empId) => {
    if (!empId || !woId) return;
    try {
      const res = await axios.get(`${API}/public/wo-clock/status/${woId}/${empId}`);
      setClockStatus(res.data);
    } catch {}
  }, [woId]);

  // Elapsed timer
  useEffect(() => {
    if (!clockStatus?.is_active || !clockStatus?.start_time) return;
    const tick = () => {
      const start = new Date(clockStatus.start_time).getTime();
      setElapsed(Math.floor((Date.now() - start) / 1000));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [clockStatus]);

  const getLocation = async () => {
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 8000 })
      );
      return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
    } catch { return null; }
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      const loc = await getLocation();
      await axios.post(`${API}/public/wo-clock/start`, {
        work_order_id: woId, employee_id: selectedEmployee, pin_code: storedPin,
        ...(loc || {}),
      });
      toast.success('Chrono démarré');
      loadStatus(selectedEmployee);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/wo-clock/stop`, {
        work_order_id: woId, employee_id: selectedEmployee, pin_code: storedPin,
      });
      toast.success(res.data.message);
      loadStatus(selectedEmployee);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const fmtElapsed = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const reset = () => {
    setAuthenticated(false);
    setEmpData(null);
    setPin('');
    setStoredPin('');
    setSelectedEmployee('');
    setClockStatus(null);
    setElapsed(0);
  };

  // === ERROR ===
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="text-xl font-bold text-white">{error}</h1>
          <p className="text-slate-400 text-sm">Scannez un QR code valide sur une fiche de travail.</p>
        </div>
      </div>
    );
  }

  // === LOADING WO ===
  if (!woInfo) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Clock className="w-8 h-8 animate-spin text-white mx-auto mb-3" />
          <p className="text-slate-300">Chargement...</p>
        </div>
      </div>
    );
  }

  // === LOGIN SCREEN ===
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-5">
          {/* Work order info header */}
          <Card className="p-4 rounded-2xl bg-slate-800 border-slate-700" data-testid="wo-info-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Fiche de travail</p>
                <p className="text-slate-400 text-xs">Pointage par fiche</p>
              </div>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <Car className="w-4 h-4 text-slate-500" />
                <span>{woInfo.vehicle_info} — {woInfo.vehicle_plate}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <User className="w-4 h-4 text-slate-500" />
                <span>{woInfo.client_name}</span>
              </div>
            </div>
          </Card>

          {/* Auth form */}
          <Card className="p-6 space-y-4 rounded-2xl">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employe</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="wo-employee-select" className="h-12 rounded-xl text-base">
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Code PIN</label>
              <div className="flex justify-center gap-3 mb-4">
                {[0,1,2,3].map(i => (
                  <div key={i} className={`w-4 h-4 rounded-full transition-all ${pin.length > i ? 'bg-slate-900 scale-110' : 'bg-slate-200'}`} />
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['1','2','3','4','5','6','7','8','9'].map(n => (
                  <Button key={n} type="button" variant="outline"
                    className="h-14 text-xl font-semibold rounded-xl"
                    onClick={() => pin.length < 4 && setPin(p => p + n)}
                    data-testid={`wo-key-${n}`}
                  >{n}</Button>
                ))}
                <Button type="button" variant="ghost"
                  className="h-14 text-sm font-medium rounded-xl text-red-500"
                  onClick={() => setPin('')}
                  data-testid="wo-key-clear"
                >Effacer</Button>
                <Button type="button" variant="outline"
                  className="h-14 text-xl font-semibold rounded-xl"
                  onClick={() => pin.length < 4 && setPin(p => p + '0')}
                  data-testid="wo-key-0"
                >0</Button>
                <Button type="button" variant="ghost"
                  className="h-14 text-lg rounded-xl"
                  onClick={() => setPin(p => p.slice(0, -1))}
                  data-testid="wo-key-back"
                >&larr;</Button>
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center gap-2 text-slate-500">
                <Clock className="w-4 h-4 animate-spin" />
                <span className="text-sm">Verification...</span>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  // === CLOCK SCREEN ===
  const isActive = clockStatus?.is_active;

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-5">
        {/* Work order info */}
        <Card className="p-4 rounded-2xl bg-slate-800 border-slate-700" data-testid="wo-clock-info">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">{woInfo.vehicle_info}</p>
              <p className="text-slate-400 text-xs">{woInfo.vehicle_plate} — {woInfo.client_name}</p>
            </div>
          </div>
        </Card>

        {/* Employee + status */}
        <div className="text-center">
          <p className="text-lg font-bold text-white">{empData?.first_name} {empData?.last_name}</p>
          <p className={`text-sm font-medium mt-1 ${isActive ? 'text-emerald-400' : 'text-slate-400'}`}>
            {isActive ? 'Chrono en cours' : 'Prêt'}
          </p>
        </div>

        {/* Timer */}
        {isActive && (
          <div className="text-center" data-testid="wo-timer">
            <div className="text-5xl font-mono font-bold text-emerald-400 tabular-nums tracking-tight">
              {fmtElapsed(elapsed)}
            </div>
          </div>
        )}

        {/* Today summary */}
        {clockStatus && clockStatus.total_today_minutes > 0 && !isActive && (
          <div className="text-center text-slate-400 text-sm" data-testid="wo-today-summary">
            Aujourd'hui: {clockStatus.entries_today} session(s) — {clockStatus.total_today_minutes} min
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3">
          {!isActive ? (
            <Button
              onClick={handleStart}
              disabled={loading}
              className="w-full h-20 rounded-2xl text-xl font-bold bg-emerald-600 hover:bg-emerald-700 shadow-lg"
              data-testid="wo-start-btn"
            >
              <Play className="w-7 h-7 mr-3" />
              {loading ? 'En cours...' : 'DEMARRER'}
            </Button>
          ) : (
            <Button
              onClick={handleStop}
              disabled={loading}
              className="w-full h-20 rounded-2xl text-xl font-bold bg-red-600 hover:bg-red-700 shadow-lg"
              data-testid="wo-stop-btn"
            >
              <Square className="w-7 h-7 mr-3" />
              {loading ? 'En cours...' : 'ARRETER'}
            </Button>
          )}
        </div>

        <Button variant="ghost" onClick={reset}
          className="w-full text-slate-500 hover:text-white text-sm"
          data-testid="wo-change-emp-btn"
        >
          Changer d'employe
        </Button>
      </div>
    </div>
  );
}
